import { useEffect, useState } from "react";
import logoPng from "../../public/logo.png";
import { Link, NavLink } from "react-router";
import blueskyLogo from "../../public/bluesky-logo.svg";
import { ChevronDown, ExternalLink, Hash, Pin, X } from "lucide-react";
import { cn } from "../lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Badge } from "./Badge";

const Header = () => {
    const [isScrollingDown, setIsScrollingDown] = useState(false);
    let lastScrollY = 0;
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [isBannerClosed, setIsBannerClosed] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > lastScrollY) {
                setIsScrollingDown(true);
            } else {
                setIsScrollingDown(false);
            }
            lastScrollY = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [lastScrollY]); // Added lastScrollY to dependencies

    useEffect(() => {
        if (isScrollingDown) {
            setDropdownOpen(false);
        }
    }, [isScrollingDown]);

    return (
        <header className={cn({ "mb-12": !isBannerClosed })}>
            {!isBannerClosed && (
                <div className="fixed top-0 left-0 right-0 h-14 bg-primary/85 text-white backdrop-blur-lg z-50 px-4 flex items-center justify-center">
                    <div className="flex items-center justify-between max-w-6xl w-full">
                        <Link
                            to="https://buymeacoffee.com/sahajjain1z/e/379721"
                            className="max-w-6xl w-full mx-auto flex items-center justify-center gap-4 group"
                        >
                            <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                                AD
                            </span>
                            <span className="font-medium group-hover:opacity-75 md:text-base hidden md:inline">
                                Reach your audience here—advertise with us
                            </span>
                            <span className="font-medium group-hover:opacity-75 md:hidden">
                                Advertise with us —
                            </span>
                            <span className="items-center gap-1 group-hover:underline flex -ml-3 md:ml-0">
                                Learn More
                                <ExternalLink className="h-4 w-4" />
                            </span>
                        </Link>
                        <X
                            className="h-4 w-4 cursor-pointer"
                            onClick={() => setIsBannerClosed(true)}
                        />
                    </div>
                </div>
            )}
            <div
                className={cn(
                    "fixed top-14 left-0 right-0 bg-white/90 backdrop-blur-sm z-40 transition-transform duration-300",
                    {
                        "-translate-y-[200px]": isScrollingDown,
                        "translate-y-0": !isScrollingDown,
                        "top-0": isBannerClosed,
                        "top-14": !isBannerClosed,
                    }
                )}
            >
                <div className="mx-auto px-4 lg:px-8 border-b border-border/50">
                    <div className="max-w-6xl mx-auto flex items-center justify-between h-14">
                        <div className="flex flex-1 items-center">
                            <NavLink
                                to="/"
                                className="flex gap-1 flex-1 items-center"
                            >
                                <img
                                    src={logoPng || "/placeholder.svg"}
                                    alt="bskyinsights"
                                    className="h-6 w-6 md:h-8 md:w-8"
                                />
                                <div className="text-base md:text-lg font-semibold">
                                    <span className="text-primary">
                                        Bluesky{" "}
                                    </span>
                                    <span className="text-primary">
                                        Insights
                                    </span>
                                </div>
                            </NavLink>

                            {/* Dropdown Menu replacing the About link */}
                            <DropdownMenu
                                modal={false}
                                open={dropdownOpen}
                                onOpenChange={setDropdownOpen}
                            >
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-sm md:text-base text-muted-foreground hover:bg-transparent hover:text-primary flex items-center gap-1"
                                    >
                                        More
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="w-56"
                                >
                                    <DropdownMenuItem asChild>
                                        <Link
                                            to="/bookmarks"
                                            className="flex items-center cursor-pointer"
                                        >
                                            <Pin className="h-4 w-4 mr-2 text-primary" />
                                            <span>Your Pinned Posts</span>
                                            <Badge
                                                variant="success"
                                                className="ml-1 text-xs py-0 px-1"
                                            >
                                                NEW
                                            </Badge>
                                        </Link>
                                    </DropdownMenuItem>

                                    <DropdownMenuItem asChild>
                                        <Link
                                            to="/realtime/hashtags"
                                            className="flex items-center cursor-pointer"
                                        >
                                            <Hash className="h-4 w-4 mr-2 text-primary" />
                                            <span>Trending Hashtags</span>
                                        </Link>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <a
                                href="https://bsky.app/profile/bskyinsights.com"
                                title="Follow us on Bluesky"
                                className="ml-4"
                            >
                                <img
                                    src={blueskyLogo || "/placeholder.svg"}
                                    alt="Bluesky"
                                    className="h-5"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;

// AD banner without ad
//
// <Link
//     to="https://buymeacoffee.com/sahajjain1z/e/379721"
//     className="max-w-6xl w-full mx-auto flex items-center justify-center gap-4 group"
// >
//     <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">
//         AD
//     </span>
//     <span className="font-medium group-hover:opacity-75 md:text-base hidden md:inline">
//         Reach your audience here—advertise with us
//     </span>
//     <span className="font-medium group-hover:opacity-75 md:hidden">
//         Advertise with us —
//     </span>
//     <span className="items-center gap-1 group-hover:underline flex -ml-3 md:ml-0">
//         Learn More
//         <ExternalLink className="h-4 w-4" />
//     </span>
// </Link>

// AD banner with ad
//
//<Link
//    to={`https://handles.net/?utm_source=bskyinsights&utm_campaign=top_banner`}
//    className="max-w-6xl w-full mx-auto flex items-center justify-center gap-4 group"
//>
//    <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">AD</span>
//    <span className="font-medium group-hover:opacity-75 md:text-base">handles.net</span>
//    <span className="font-normal group-hover:opacity-75 md:text-base -ml-3 hidden md:inline text-ellipsis overflow-hidden whitespace-nowrap">
//        — Automatically provision and manage Bluesky handles for your team or community using handles.net
//    </span>
//    <span className="items-center gap-1 group-hover:opacity-75 flex md:ml-0 mt-[1px] md:mt-0">
//        <span className="text-xs md:hidden">Learn More</span>
//        <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
//    </span>
//</Link>
